/**
 * Browsers fire a window "error" when ResizeObserver notifications cannot all
 * be delivered in the same animation frame. This is a known benign browser
 * notification (not an app crash), commonly triggered by UI libraries that
 * observe layout (Ant Design, MUI, charts, react-select, etc.).
 *
 * Covers both:
 * - `npm start` (CRA / webpack-dev-server runtime overlay)
 * - `npm run build` + static hosting (suppresses the error event; no overlay in prod)
 *
 * Only this ResizeObserver message family is swallowed; real errors still surface.
 */

const RESIZE_OBSERVER_ERROR_PATTERN =
  /ResizeObserver loop (limit exceeded|completed with undelivered notifications)/i;

const WEBPACK_OVERLAY_IDS = [
  "webpack-dev-server-client-overlay",
  "webpack-dev-server-client-overlay-div",
];

export function isResizeObserverLoopError(message) {
  return typeof message === "string" && RESIZE_OBSERVER_ERROR_PATTERN.test(message);
}

function dismissWebpackResizeObserverOverlay() {
  WEBPACK_OVERLAY_IDS.forEach((id) => {
    const element = document.getElementById(id);
    if (!element) return;

    const text = element.textContent || "";
    if (RESIZE_OBSERVER_ERROR_PATTERN.test(text)) {
      element.remove();
    }
  });
}

function watchAndDismissWebpackOverlay() {
  if (typeof MutationObserver === "undefined" || !document.documentElement) {
    return;
  }

  const observer = new MutationObserver(() => {
    dismissWebpackResizeObserverOverlay();
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  // Dev overlay is only relevant early; stop watching after app settles.
  window.setTimeout(() => observer.disconnect(), 15000);
}

export function suppressResizeObserverError() {
  if (typeof window === "undefined" || window.__kaarujResizeObserverErrorSuppressed) {
    return;
  }

  window.__kaarujResizeObserverErrorSuppressed = true;

  watchAndDismissWebpackOverlay();

  window.addEventListener(
    "error",
    (event) => {
      if (!isResizeObserverLoopError(event.message)) {
        return;
      }

      event.stopImmediatePropagation();
      event.preventDefault();
      dismissWebpackResizeObserverOverlay();
    },
    // Capture phase so we run before webpack / CRA overlay listeners.
    true
  );

  const previousOnError = window.onerror;
  window.onerror = function onError(message, source, lineno, colno, error) {
    if (isResizeObserverLoopError(String(message))) {
      dismissWebpackResizeObserverOverlay();
      return true;
    }

    if (typeof previousOnError === "function") {
      return previousOnError(message, source, lineno, colno, error);
    }

    return false;
  };
}
